import React, { useState, useEffect, useLayoutEffect } from 'react';
import { getContext, useCombinedRefs, getEasing, getDuration } from 'common';
import _debounce from 'lodash/debounce';
import _merge from 'lodash/merge';
import { useSpring } from 'react-spring';
import Elevation, { ElevationProps } from 'components/Elevation';
import { SPEED_FAST } from '@nokia-csf-uxr/freeform-design-tokens/tokens/speed';
import { TRANSITION_ACCELERATE } from '@nokia-csf-uxr/freeform-design-tokens/tokens/transition';
import IconButton from 'components/IconButton';
import ChevronUpIcon from '@nokia-csf-uxr/ccfk-assets/ChevronUpIcon';
import ChevronDownIcon from '@nokia-csf-uxr/ccfk-assets/ChevronDownIcon';
import 'scroll-behavior-polyfill';/* for IE11/EDGE support of element.scroll */
import Styled from './styles';
import { TabsContext } from './Tabs';
import handleKeyDown from './util/keyboardNav';

// This is the height of scroll button in pixel. 3.5rem * 16
const SCROLL_BUTTON_HEIGHT = 3.5 * 16;


export interface VerticalTabsProps extends React.ComponentPropsWithRef<'div'> {
  /** Align to the left or center (use center for smaller container). */
  alignment?: 'top' | 'center' | 'bottom';
  /** Enables scrollable tabs (enabled by default) */
  scroll?: boolean;
  /** Right-to-left. It's preferred that RTL is toggled at an application level. However if you need a component level override, this prop can help. */
  rtl?: boolean;
  /** Render prop to render custom selected-indicator. { selectedRef, innerRef, containerRef } */
  renderSelectedIndicator?(...args: unknown[]): React.ReactNode;
  /** Accepts multiple <Tab /> children. */
  children: React.ReactNode;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** Props will be passed into the most outer Elevation component */
  elevationProps?: Partial<ElevationProps>;
  /** To override the down button. */
  renderDownButton?(any): JSX.Element;
  /** To override the top button. */
  renderTopButton?(any): JSX.Element;
  /** @ignore specify variant of tabs */
  variant?: string;
}


/** Use VerticalTabs to navigate between different views.
 * Any other props than explicitly defined will be provided to the `VerticalTabs` wrapper wrapped by Elevation component.
 * To get the ref of the wrapper with `TabsInner` class name, use `ref` prop. [Read React useRef hook documentation](https://reactjs.org/docs/hooks-reference. */
const VerticalTabs = React.forwardRef<HTMLElement, VerticalTabsProps>((props: VerticalTabsProps, ref) =>
  getContext(({ RTL = false, theme: { HORIZONTAL_NAVIGATION_TAB: tabTheme, ICON_BUTTON: iconButtonTheme } }) => {
    const {
      alignment,
      scroll,
      rtl,
      renderSelectedIndicator,
      children,
      theme: userTheme,
      variant,
      elevationProps,
      ...otherProps
    } = props;
    const [selectedRef, setSelectedRef] = useState();
    const [selectedDims, setSelectedDims] = useState<React.CSSProperties | undefined>();
    const [isScrollable, setIsScrollable] = useState<{
      canScrollTop?: boolean,
      canScrollDown?: boolean
    } | false>(false);
    const [, setY] = useSpring(() => ({ y: 0 }));
    const innerRef = React.useRef(null);
    const containerRef = React.useRef(null);
    const leftButtonRef = React.useRef(null);
    const rightButtonRef = React.useRef(null);
    const selectedIndicator = React.useRef(null);
    const combinedRef = useCombinedRefs(ref, innerRef);
    const isRTL = rtl || RTL;
    const updateSelectedIndicator = _debounce(() => {
      if (selectedRef && !renderSelectedIndicator) {
        const { offsetTop: top, clientHeight: height } = selectedRef;
        setSelectedDims({ top, height });
      }
    }, 100);

    const updateScrollable = _debounce(() => {
      const { scrollHeight, clientHeight, scrollTop } = combinedRef.current;
      if (scroll && !props.renderTopButton && !props.renderDownButton) {
        /** Legacy Edge Browser interprets scrollLeft differently to chrome. */
        const canScrollTop = scrollTop > 0;
        const canScrollDown = scrollHeight > clientHeight + scrollTop;
        setIsScrollable({ canScrollTop, canScrollDown });
      }
    }, 100);

    const scrollTo = direction => () => {
      const element = combinedRef && combinedRef.current;
      const scrollDistance = containerRef.current.clientHeight * 0.75;
      const totalScrollDistance = (direction === 'down' ? scrollDistance : -scrollDistance) - SCROLL_BUTTON_HEIGHT;
      element &&
        setY({
          config: { duration: getDuration(SPEED_FAST), easing: getEasing(TRANSITION_ACCELERATE) },
          y: element.scrollTop + totalScrollDistance,
          // @ts-expect-error: Review the API of setY
          onFrame: ({ y }) => {
            element.scroll(0, y);
          }
        });
    };

    // Scroll to selected tab
    const scrollToSelected = (tab) => {
      if (!tab || !scroll || !(document && document.scrollingElement)) return;
      tab.scrollIntoView({ behavior: 'smooth', inline: 'nearest' });
    };

    // Set selected tab, and scroll to it if needed.
    const setSelectedRefFunc = (tab) => {
      setSelectedRef(tab);
      scrollToSelected(tab);
    };
    const setSelectedDimsFunc = (dims) => {
      if (dims) {
        const { width, left } = dims;
        setSelectedDims({ left, width });
      }
    };

    const setFocusedRefFunc = (tab) => {
      // to keep focused Tab in viewport when tabbing between VerticalTabs
      if (variant !== 'horizontal-navigation') {
        scrollToSelected(tab);
      }
    };

    // Animate selected tab indicator
    useEffect(() => {
      updateSelectedIndicator();
      window.addEventListener('resize', updateSelectedIndicator);
      return () => {
        window.removeEventListener('resize', updateSelectedIndicator);
      };
    }, [isRTL, selectedRef, isScrollable, props]);

    // eslint-disable-next-line consistent-return
    useLayoutEffect(() => {
      if (scroll) {
        updateScrollable();
        combinedRef.current.addEventListener('scroll', updateScrollable);
        window.addEventListener('resize', updateScrollable);
        return () => {
          combinedRef.current.removeEventListener('scroll', updateScrollable);
          window.removeEventListener('resize', updateScrollable);
        };
      }
    }, [isRTL, ref, scroll]);


    const resolvedTheme = _merge({}, tabTheme, userTheme);
    const renderTopButton = () => {
      if (scroll && isScrollable && isScrollable.canScrollTop) {
        return (
          <Styled.TabScrollButtonContainer
            vertical
            ref={leftButtonRef}
            theme={resolvedTheme}
            data-test="ccfk-tabs-up-scroll-button"
          >
            <IconButton size="small" theme={iconButtonTheme.FLAT} onClick={scrollTo('top')}>
              <ChevronUpIcon />
            </IconButton>
          </Styled.TabScrollButtonContainer>
        );
      }
      return (
        props.renderTopButton &&
        props.renderTopButton({
          isScrollable,
          selectedRef,
          innerRef: combinedRef,
          containerRef: combinedRef
        })
      );
    };

    const renderDownButton = () => {
      if (scroll && isScrollable && isScrollable.canScrollDown) {
        return (
          <Styled.TabScrollButtonContainer
            vertical
            ref={rightButtonRef}
            theme={resolvedTheme}
            data-test="ccfk-tabs-down-scroll-button"
          >
            <IconButton size="small" theme={iconButtonTheme.FLAT} onClick={scrollTo('down')}>
              <ChevronDownIcon />
            </IconButton>
          </Styled.TabScrollButtonContainer>
        );
      }
      return (
        props.renderDownButton &&
        props.renderDownButton({
          isScrollable,
          selectedRef,
          innerRef: combinedRef,
          containerRef: combinedRef
        })
      );
    };

    const renderTheSelectedIndicator = () =>
      // eslint-disable-next-line react/prop-types
      renderSelectedIndicator ? (
        renderSelectedIndicator({ selectedRef, innerRef: combinedRef, containerRef })
      ) : (
        <Styled.TabsSelectedIndicator
          theme={resolvedTheme}
          vertical
          style={{ ...selectedDims }}
          variant={variant}
          ref={selectedIndicator}
          data-test="ccfk-tabs-selected-indicator"
        />
      );
    return (
      <Elevation dark elevationIndex={0} style={{ display: 'inline-flex' }} {...elevationProps}>
        <Styled.VerticalTabs
          ref={containerRef}
          data-test="ccfk-tabs-container"
          role="tablist"
          variant={variant}
          theme={resolvedTheme}
          {...otherProps}
          rtl={isRTL}
          onKeyDown={handleKeyDown(props, combinedRef, true)}
          alignment={alignment}
        >
          <TabsContext.Provider
            value={{
              setSelectedRef: setSelectedRefFunc,
              setSelectedDims: setSelectedDimsFunc,
              selectedRef,
              variant,
              vertical: true,
              rtl: isRTL,
              setFocusedRef: setFocusedRefFunc
            }}
          >
            {renderTopButton()}
            <Styled.TabsInner
              data-test="ccfk-tabs-inner-container"
              ref={combinedRef}
              vertical
              scrollable={isScrollable && scroll}
            >
              {children}
              {renderTheSelectedIndicator()}
            </Styled.TabsInner>
            {renderDownButton()}
          </TabsContext.Provider>
        </Styled.VerticalTabs>
      </Elevation>
    );
  }));

VerticalTabs.displayName = 'VerticalTabs';
VerticalTabs.defaultProps = {
  alignment: 'top',
  scroll: true,
  rtl: false,
  renderSelectedIndicator: undefined,
  theme: {},
  variant: undefined,
  renderDownButton: undefined,
  renderTopButton: undefined,
  elevationProps: {}
};

/* Exports */
export { VerticalTabs as default };
